import { Box, Divider, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import { BsTrash } from 'react-icons/bs'
import { GrPowerCycle } from 'react-icons/gr'
import Loading from 'react-loading'
import { useSelector } from 'react-redux'
import * as Yup from 'yup'
import Inventory from '../../../../../../api/db/Inventory'
import { IFrozenInventoryItem, IUpdateFormProps } from '../../../../../../shared/types'
import { selectCurrrentAdminShop } from '../../../../../../store/features/currentBusinessShop'
import { selectUserPropsState } from '../../../../../../store/features/user'
import { ValidatedButton, ValidatedForm, ValidatedTextInput } from '../../../../../organisms/validation/admin'
import { Button } from '../../../../../styled'

interface IUpdateFrozenItemProps extends IUpdateFormProps{
    item_data: IFrozenInventoryItem
}

const validationSchema = Yup.object().shape({

    name: Yup.string().required().label("name"),
    brand: Yup.string().required().label("brand"),
    cartons: Yup.number().required().label("cartons"),
    cost_price_per_carton: Yup.number().required().label("cost_price_per_carton"),
    selling_price_per_carton: Yup.number().required().label("selling_price_per_carton"),
    selling_price_per_kilo: Yup.number().required().label("selling_price_per_kilo"),
    carton_type: Yup.string().label("carton_type"),
})

export default function UpdateProductForm({item_data, onDelete, onDeleteFail,onUpdate, onUpdateFail}:IUpdateFrozenItemProps) {

    const [isUpdating, setIsUpdating] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const current_user = useSelector(selectUserPropsState)
    const current_shop = useSelector(selectCurrrentAdminShop)?.name

    const initialValues = {

        name: item_data.name,
        brand: item_data.brand,
        cartons: item_data.cartons,
        kilos_per_carton: item_data.kilos_per_carton,
        cost_price_per_carton: item_data.cost_price_per_carton,
        selling_price_per_carton: item_data.selling_price_per_carton,
        selling_price_per_kilo: item_data.selling_price_per_kilo,
        carton_type: item_data.carton_type
        
    }

    const handleOnSubmit = async (product: typeof initialValues)=>{

        try {

            const _product = {
                ...product,
                cartons: parseFloat(product.cartons.toString()),
                kilos_per_carton: parseFloat(product.kilos_per_carton.toString()),
                cost_price_per_carton: parseFloat(product.cost_price_per_carton.toString()),
                selling_price_per_carton: parseFloat(product.selling_price_per_carton.toString()),
                selling_price_per_kilo: parseFloat(product.selling_price_per_kilo.toString())
            }

            setIsUpdating(true)
            await Inventory.addProduct(_product, current_user!.uid, current_shop!, current_user!)
            setIsUpdating(false)
            onUpdate()
            
        } catch (error) {
            setIsUpdating(false)
            onUpdateFail(error)
            throw error
        }
        
    }

    const handleOnDelete = async ()=>{
        try {

            setIsDeleting(true)
            await Inventory.deleteProduct(item_data.name, current_user?.uid!, current_shop!)
            onDelete()
            setIsDeleting(false)
            
        } catch (error) {
            setIsDeleting(false)
            onDeleteFail(error)
        }
    }
    
    return (
        <ValidatedForm
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleOnSubmit}
        >

            <Box
            >
                <Box
                    sx={{width: "100%", height: "20rem", overflow: "auto"}}
                >
                    <Stack 
                        direction="row"
                        sx={{width: "100%", marginBottom: "1rem"}}
                        spacing={2}
                    >
                        <Stack sx={{width: "100%"}}>
                            <Typography variant="subtitle2" color="grey.500">Product name</Typography>
                            <ValidatedTextInput readOnly name="name" type="text" />
                        </Stack>
                        <Stack sx={{width: "100%"}}>
                            <Typography variant="subtitle2" color="grey.500">Brand</Typography>
                            <ValidatedTextInput name="brand" type="text" />
                        </Stack>
                    </Stack>
                    <Divider />
                    <Stack 
                        direction="row"
                        sx={{width: "100%", marginBottom: "1rem"}}
                        spacing={2}
                    >
                        <Stack sx={{width: "100%"}}>
                            <Typography variant="subtitle2" color="grey.500">Cartons</Typography>
                            <ValidatedTextInput readOnly name="cartons" type="text" />
                        </Stack>
                        <Stack sx={{width: "100%"}}>
                            <Typography variant="subtitle2" color="grey.500">Kilos Per Carton</Typography>
                            <ValidatedTextInput name="kilos_per_carton" type="text" />
                        </Stack>
                    </Stack>
                    <Divider />
                    <Stack 
                        direction="row"
                        sx={{width: "100%", marginBottom: "1rem"}}
                        spacing={2}
                    >
                        <Stack sx={{width: "100%"}}>
                            <Typography variant="subtitle2" color="grey.500">Cost Price Per Carton</Typography>
                            <ValidatedTextInput name="costprice_per_carton" type="number" />
                        </Stack>
                        <Stack sx={{width: "100%"}}>
                            <Typography variant="subtitle2" color="grey.500">Selling Price Per Carton</Typography>
                            <ValidatedTextInput name="sellingprice_per_carton" type="number" />
                        </Stack>
                    </Stack>
                    <Divider />
                    <Stack 
                        direction="row"
                        sx={{width: "100%", marginBottom: "1rem"}}
                        spacing={2}
                    >
                        <Stack sx={{width: "100%"}}>
                            <Typography variant="subtitle2" color="grey.500">Selling Price Per Kilo</Typography>
                            <ValidatedTextInput name="selling_price_per_kilo" type="number" />
                        </Stack>
                        <Stack sx={{width: "100%"}}>
                            <Typography variant="subtitle2" color="grey.500">Carton Type</Typography>
                            <ValidatedTextInput name="carton_type" type="number" />
                        </Stack>
                    </Stack>
                </Box>

                <Box
                    sx={{marginTop: "1.5rem", display: "flex", justifyContent: "flex-end"}}
                >
                    
                    <Stack direction={'row'} spacing={1}>
                        <Button 
                            color='error' 
                            variant='contained'
                            disableElevation={true}
                            onClick={handleOnDelete}
                            disabled={isUpdating || isDeleting}
                        >
                            {!isDeleting ? (<BsTrash className='text-white mr-1' />) : (
                                <Loading type="spin" className='mr-1' width="1rem" height="1rem" color="#fff" />
                            )}
                            Delete
                        </Button>
                        
                        <ValidatedButton 
                            startIcon={<GrPowerCycle />} 
                            variant="contained" 
                            color="secondary" 
                            disableElevation={true}
                            loading={isUpdating}
                            disabled={isUpdating || isDeleting}
                        >
                            <Typography  color="primary" sx={{fontSize:"13px"}} >Update</Typography>
                        </ValidatedButton>
                    </Stack>
                </Box>

            </Box>
            
        </ValidatedForm>
    )
}
